import {
  useCallback,
  useRef,
  useState,
  type RefObject,
} from 'react';
import { clampRectToViewport } from './Cmarkdowntable.helpers';
import type { CmarkdowntablePopoverRect } from './Cmarkdowntable.types';

interface CmarkdowntablePopoverManualRectController {
  activeAnchorRef: RefObject<HTMLDivElement | null>;
  popoverRef: RefObject<HTMLDivElement | null>;
  manualRectRef: RefObject<CmarkdowntablePopoverRect | null>;
  manualRect: CmarkdowntablePopoverRect | null;
  applyManualRect: (rect: CmarkdowntablePopoverRect | null) => void;
  clearManualRect: () => void;
  clampManualRectToViewport: () => void;
  restoreRememberedManualRect: () => boolean;
}

export function useCmarkdowntablePopoverManualRect(): CmarkdowntablePopoverManualRectController {
  const activeAnchorRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const manualRectRef = useRef<CmarkdowntablePopoverRect | null>(null);
  const lastManualRectRef = useRef<CmarkdowntablePopoverRect | null>(null);
  const [manualRect, setManualRect] = useState<CmarkdowntablePopoverRect | null>(null);

  const applyManualRect = useCallback((rect: CmarkdowntablePopoverRect | null): void => {
    manualRectRef.current = rect;
    if (rect) {
      lastManualRectRef.current = rect;
    }
    setManualRect(rect);
  }, []);

  const clearManualRect = useCallback((): void => {
    activeAnchorRef.current = null;
    applyManualRect(null);
  }, [applyManualRect]);

  const clampManualRectToViewport = useCallback((): void => {
    const current = manualRectRef.current;
    if (!current) {
      return;
    }

    applyManualRect(clampRectToViewport({
      rect: current,
      viewportWidth: document.documentElement.clientWidth,
      viewportHeight: document.documentElement.clientHeight,
    }));
  }, [applyManualRect]);

  const restoreRememberedManualRect = useCallback((): boolean => {
    const remembered = lastManualRectRef.current;
    if (!remembered) {
      return false;
    }

    applyManualRect(clampRectToViewport({
      rect: remembered,
      viewportWidth: document.documentElement.clientWidth,
      viewportHeight: document.documentElement.clientHeight,
    }));
    return true;
  }, [applyManualRect]);

  return {
    activeAnchorRef,
    popoverRef,
    manualRectRef,
    manualRect,
    applyManualRect,
    clearManualRect,
    clampManualRectToViewport,
    restoreRememberedManualRect,
  };
}
